/**
 * Classify a page's referrer into a traffic source, isolating AI answer engines
 * (the GEO signal) from classic search and everything else.
 *
 * WHY: "Is any dealer storefront getting cited by ChatGPT / Perplexity / etc.?"
 * is unanswerable without capturing WHERE a visitor came from. Answer engines
 * that run their own domain (chatgpt.com, perplexity.ai, claude.ai, …) leave a
 * distinct `document.referrer` when a user clicks a citation — that's the signal.
 * Google AI Overviews are NOT separable here: an AI-Overview click and a normal
 * Google search click both referer `google.com`, so both fall under 'search'.
 *
 * Consumed by the storefront analytics client (first-touch capture into
 * analytics_events.metadata.referrer_source). When a Vercel-bundled portal
 * function needs this too, inline a copy per the repo's inlined-utility rule —
 * it has no consumer there yet, so no inline copy exists.
 */

/** AI answer engines with their own referrer domain — the GEO signal. */
export const AI_ANSWER_ENGINE_SOURCES = [
  'chatgpt',
  'perplexity',
  'claude',
  'gemini',
  'copilot',
  'you',
] as const

export type AiAnswerEngineSource = (typeof AI_ANSWER_ENGINE_SOURCES)[number]

/** All referrer-source labels. 'search' = classic search engine; 'other' = any
 *  other external site. Same-origin / empty / unparseable referrers → null. */
export type ReferrerSource = AiAnswerEngineSource | 'search' | 'other'

const AI_EXACT: Record<string, AiAnswerEngineSource> = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'claude.ai': 'claude',
  'gemini.google.com': 'gemini',
  'bard.google.com': 'gemini',
  'copilot.microsoft.com': 'copilot',
  'you.com': 'you',
}

function normHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, '')
}

function isSearchHost(h: string): boolean {
  if (
    h === 'bing.com' ||
    h === 'duckduckgo.com' ||
    h === 'search.brave.com' ||
    h === 'ecosia.org' ||
    h === 'search.yahoo.com'
  ) {
    return true
  }
  // Any Google search TLD (google.com, google.co.uk, …). AI subdomains
  // (gemini./bard.google.com) are matched by AI_EXACT first, so they never
  // reach here.
  return h === 'google.com' || /^google\.[a-z.]+$/.test(h)
}

/**
 * @param referrer   `document.referrer` (may be empty on direct visits).
 * @param currentHost the storefront's own host (`location.host`), to drop
 *                    same-origin internal navigations. Port is stripped.
 * @returns the source label, or null for direct / internal / unparseable.
 */
export function classifyReferrerSource(
  referrer: string | null | undefined,
  currentHost: string | null | undefined,
): ReferrerSource | null {
  if (!referrer) return null
  let host: string
  try {
    host = new URL(referrer).hostname
  } catch {
    return null
  }
  if (!host) return null
  const h = normHost(host)
  const cur = normHost((currentHost ?? '').split(':')[0])
  if (cur && h === cur) return null // internal navigation, not a traffic source
  if (AI_EXACT[h]) return AI_EXACT[h]
  if (isSearchHost(h)) return 'search'
  return 'other'
}
